import { useEffect, useMemo, useRef, useState } from 'react';
import { sendEvidencePhrase } from '@/lib/sendEvidence';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { invoiceChangePlanSchema, type InvoiceChangeApplyRequest,
  type InvoiceChangeApplyResult, type InvoiceChangeIntent,
  type InvoiceChangePlan } from '@/lib/invoiceSelectiveHistoryContracts';
import { Button } from '@/components/ui/button';
import { ReservationReconcileList } from './PaymentReconciliationPanels';
import { parseBlockingReservations, type BlockingReservation } from '@/lib/invoice-payment-reconciliation';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export interface InvoiceChangeReviewProps {
  intent: InvoiceChangeIntent;
  focusedInvoiceId?: string;
  focusedProjectIds?: string[];
  onClose: () => void;
  onApplied: (result: InvoiceChangeApplyResult) => void;
}

type ApiFailure = Error & { status?: number; data?: {
  error?: { code?: string; comparison?: unknown };
  detail?: { code?: string; comparison?: unknown };
} };

const fieldNames = ['legal_name', 'dba', 'billing_address'] as const;
const EMPTY_PROJECT_IDS: string[] = [];
type RecipientEffects = NonNullable<InvoiceChangePlan['candidates'][number]['recipient_effects']> & {
  kept_on_move?: NonNullable<InvoiceChangePlan['candidates'][number]['recipient_effects']>['removed_other_linked'];
};
type FutureDefaultRow = InvoiceChangePlan['selected_project_defaults'][number];

function defaultState(row: FutureDefaultRow, side: 'before' | 'after') {
  const value = row[side];
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function defaultTarget(row: FutureDefaultRow) {
  if (row.client_scope_id || row.project_id) {
    const projectName = row.project_name?.trim();
    const siteAddress = row.project_address?.trim();
    return { name: projectName || (siteAddress ? `Unnamed project · Site address: ${siteAddress}` : null),
      missingLabel: siteAddress ? `Project name unavailable · Site address: ${siteAddress}` : 'Project name unavailable',
      isProject: true };
  }
  if (row.organization_id) {
    return { name: row.organization_name?.trim() || null, missingLabel: 'Client name unavailable', isProject: false };
  }
  return { name: null, missingLabel: 'Default target unavailable', isProject: false };
}

function defaultStateDescription(row: FutureDefaultRow, side: 'before' | 'after', field: string) {
  const state = defaultState(row, side);
  if (field === 'hide_dba') {
    return state.hide_dba === true ? 'DBA hidden on invoice PDFs'
      : state.hide_dba === false ? 'DBA shown on invoice PDFs'
        : 'Inherit current client PDF DBA visibility (resolved when an invoice is created)';
  }
  if (field === 'dba') {
    const name = side === 'before' ? row.before_establishment_name : row.after_establishment_name;
    if (name) return name;
    if (row.client_scope_id || row.project_id) {
      return state.default_establishment_cleared === true
        ? 'No DBA (project default explicitly cleared)'
        : 'Inherit the client’s current DBA';
    }
    return 'No client establishment configured';
  }
  if (field === 'billing_address') {
    const source = typeof state.source === 'string' ? state.source : null;
    const override = typeof state.override === 'string' ? state.override : null;
    if (source === 'custom') return `Custom address: ${override || '(empty)'}`;
    if (source === 'project') return 'Project address (resolved when an invoice is created)';
    if (source === 'establishment') return 'Establishment address (resolved when an invoice is created)';
    if (source === 'organization') return 'Client billing address (resolved when an invoice is created)';
    return 'Inherit the existing address source (resolved when an invoice is created)';
  }
  return null;
}

function futureDefaultChanges(row: FutureDefaultRow, intent: InvoiceChangeIntent) {
  const field = row.field ?? (intent.intent === 'identity_default' ? intent.default_field : undefined);
  const changes: string[] = [];
  if (row.before_billing_address !== undefined || row.after_billing_address !== undefined) {
    const label = row.source_field === 'site_address' ? 'Site address' : 'Billing address';
    changes.push(`${label}: ${row.before_billing_address || '(empty)'} → ${row.after_billing_address || '(empty)'}`);
  }
  if (row.before_organization_name !== undefined || row.after_organization_name !== undefined) {
    changes.push(`Future client: ${row.before_organization_name || 'Client name unavailable'} → ${row.after_organization_name || 'Client name unavailable'}`);
  }
  if (field && ['billing_address', 'dba', 'hide_dba'].includes(field)) {
    const before = defaultStateDescription(row, 'before', field);
    const after = defaultStateDescription(row, 'after', field);
    if (before && after) changes.push(`${field === 'hide_dba' ? 'PDF DBA visibility' : field === 'dba' ? 'DBA' : 'Billing address'}: ${before} → ${after}`);
  }
  return changes;
}

function recipientDescription(recipient: RecipientEffects['retained_typed_in'][number]) {
  return [recipient.name, recipient.email, recipient.role].filter(Boolean).join(' · ')
    || 'Saved recipient details unavailable';
}

function recipientImpact(effects: RecipientEffects | null | undefined) {
  if (!effects) return null;
  const retained = [...effects.retained_typed_in, ...effects.retained_target_contacts];
  const removed = effects.removed_other_linked;
  const staying = effects.kept_on_move ?? [];
  if (retained.length === 0 && removed.length === 0 && staying.length === 0) return null;
  return <section className="ml-7 mt-2 rounded bg-muted/40 p-2 text-sm" aria-label="Recipient impact">
    {retained.length > 0 && <div>
      <p className="font-medium">Retained recipients</p>
      <ul>{retained.map((recipient) => <li key={recipient.invoice_contact_id}>
        {recipientDescription(recipient)}
        {recipient.contact_id === null && <span className="text-muted-foreground"> · entered on this invoice</span>}
      </li>)}</ul>
    </div>}
    {staying.length > 0 && <div className={retained.length > 0 ? 'mt-2' : ''}>
      <p className="font-medium">Staying on this invoice</p>
      <ul>{staying.map((recipient) => <li key={recipient.invoice_contact_id}>{recipientDescription(recipient)}</li>)}</ul>
    </div>}
    {removed.length > 0 && <div className={retained.length > 0 || staying.length > 0 ? 'mt-2' : ''}>
      <p className="font-medium">Recipients to remove</p>
      <ul>{removed.map((recipient) => <li key={recipient.invoice_contact_id}>{recipientDescription(recipient)}</li>)}</ul>
    </div>}
  </section>;
}

function describedChanges(row: InvoiceChangePlan['candidates'][number]) {
  const lines = fieldNames.flatMap((field) => {
    const before = row.before.pdf[field].value;
    const after = row.after.pdf[field].value;
    return before === after ? [] : [`${field.replace('_', ' ')}: ${before || '(empty)'} → ${after || '(empty)'}`];
  });
  if (row.before.client_display_name.value !== row.after.client_display_name.value) {
    lines.push(`Client display name: ${row.before.client_display_name.value || '(empty)'} → ${row.after.client_display_name.value || '(empty)'}`);
  }
  return lines;
}

function followDefaultDetails(row: InvoiceChangePlan['candidates'][number]
  | InvoiceChangePlan['source_actions'][number], intent: InvoiceChangeIntent) {
  if (intent.intent !== 'follow_default') return null;
  const lines = intent.follow_fields.map((field) => {
    const before = field === 'display_name' ? row.before.client_display_name : row.before.pdf[field];
    const after = field === 'display_name' ? row.after.client_display_name : row.after.pdf[field];
    const source = after.source.replaceAll('_', ' ');
    return `${field.replace('_', ' ')} now follows ${source}: ${before.value || '(empty)'} → ${after.value || '(empty)'}`;
  });
  return <section className="ml-7 mt-2 rounded bg-muted/40 p-2 text-sm" data-testid={`follow-default-details-${row.invoice_id}`}>
    <p>This choice changes the current invoice. Defaults for newly created invoices are separate and unchanged.</p>
    <ul>{lines.map((line) => <li key={line}>{line}</li>)}</ul>
  </section>;
}

type Candidate = InvoiceChangePlan['candidates'][number];

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const projectLabelOf = (number?: string | null, name?: string | null) =>
  [number, name].filter(Boolean).join(' ') || 'this project';
const listNames = (names: string[]) => names.length <= 1 ? names.join('')
  : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * R2 AC7: how a move treats an invoice that already has a recorded send or a payment, in plain words.
 * The printed name is what the invoice prints now (its legal name, the DBA's when it has one), which is what the
 * client already has unless the invoice changed after the send.
 */
function carefulLines(row: Candidate): string[] {
  const lines: string[] = [];
  const iss = row.issuance_provenance;
  if (iss.kind !== 'unknown') {
    const printed = row.before.pdf.legal_name.value || row.before.client_display_name.value;
    // R3 AC5/AC9: the same fixed phrase as the invoice page and the send history, with a name and never an email address.
    const phrase = sendEvidencePhrase({ kind: iss.kind, at: iss.at, by: 'by' in iss ? iss.by : null,
      by_name: 'by_name' in iss ? iss.by_name : null });
    lines.push(`${phrase}. The client already has a copy addressed to ${printed}.`);
  }
  if (row.status === 'paid') lines.push('Paid. Changing the client changes who the receipt is for.');
  if (row.status === 'partially_paid') lines.push('Partly paid. Changing the client changes who the receipt is for.');
  return lines;
}

export function InvoiceChangeReview({ intent, focusedInvoiceId, focusedProjectIds = EMPTY_PROJECT_IDS,
  onClose, onApplied }: InvoiceChangeReviewProps) {
  const queryClient = useQueryClient();
  const [plan, setPlan] = useState<InvoiceChangePlan | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedDefaults, setSelectedDefaults] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [acknowledgedRecipientRemovals, setAcknowledgedRecipientRemovals] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [showOtherProjects, setShowOtherProjects] = useState(false);
  // R2 AC9: which existing target billing track to use, per source track, when the target has several.
  const [trackChoices, setTrackChoices] = useState<Record<string, string>>({});
  // R2 AC10: saved people of the moved project she ticked "Keep on {project} invoices" (none by default).
  const [keepIds, setKeepIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // R5 iteration 4: payment links with an unconfirmed outcome that block this change (staff can check them with the provider).
  const [blockedReservations, setBlockedReservations] = useState<BlockingReservation[]>([]);
  const [stale, setStale] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<InvoiceChangeApplyRequest | null>(null);
  const baseIntentKey = JSON.stringify(intent);
  const projectKey = [...focusedProjectIds].sort().join('|');
  const choicesKey = JSON.stringify(trackChoices);
  const isMove = intent.intent === 'project_assignment';
  // The intent actually reviewed and applied: the one she was given plus the billing-track choice she made.
  const intentKey = useMemo(() => {
    const choices = JSON.parse(choicesKey) as Record<string, string>;
    const base = JSON.parse(baseIntentKey) as InvoiceChangeIntent;
    if (!Object.keys(choices).length) return baseIntentKey;
    if (base.intent === 'project_assignment') return JSON.stringify({ ...base, target_client_scope_choices: choices });
    if (base.intent === 'invoice_assignment') return JSON.stringify({ ...base, target_client_scope_id: Object.values(choices)[0] });
    return baseIntentKey;
  }, [baseIntentKey, choicesKey]);
  const effectiveIntent = useMemo(() => JSON.parse(intentKey) as InvoiceChangeIntent, [intentKey]);
  const lastBaseRef = useRef<string | null>(null);

  useEffect(() => {
    let live = true;
    const reviewedIntent = JSON.parse(intentKey) as InvoiceChangeIntent;
    const projectSet = new Set(projectKey ? projectKey.split('|') : []);
    // Choosing a billing track re-reviews the same change; her ticks stay. Any other change starts fresh.
    const signature = `${baseIntentKey}|${projectKey}|${focusedInvoiceId ?? ''}`;
    // Only a plan that actually loaded counts (a development double-run of this effect must not look like a re-review).
    const sameChange = lastBaseRef.current === signature;
    setAcknowledged([]);
    setAcknowledgedRecipientRemovals([]);
    setLoading(true);
    setError(null);
    api.invoices.previewChange(reviewedIntent).then((fresh) => {
      if (!live) return;
      lastBaseRef.current = signature;
      setPlan(fresh);
      const validIds = new Set([...fresh.candidates.map((row) => row.invoice_id),
        ...fresh.source_actions.map((row) => row.invoice_id)]);
      // R2 AC7: on a project move, unsent invoices are ticked; an invoice with a recorded send or a payment is
      // listed unticked (she can tick it). The invoice she opened the flow from stays ticked: she asked for it.
      const careful = (row: Candidate) => row.requires_post_send_acknowledgement
        || row.status === 'paid' || row.status === 'partially_paid';
      // R5 AC5: a cancelled invoice is listed but not ticked (the preview's `default_selected` is false for it).
      const defaultTicks = fresh.candidates.filter((row) => row.invoice_id === focusedInvoiceId
        || (projectSet.has(row.project_id) && row.default_selected !== false
          && !(reviewedIntent.intent === 'project_assignment' && careful(row))))
        .map((row) => row.invoice_id);
      if (sameChange) {
        setSelected((prior) => prior.filter((id) => validIds.has(id)));
      } else {
        setSelected(defaultTicks);
        // Source-only actions are still selectable, even with zero material candidates.
        if (focusedInvoiceId && fresh.source_actions.some((row) => row.invoice_id === focusedInvoiceId)) {
          setSelected((prior) => [...new Set([...prior, focusedInvoiceId])]);
        }
      }
      const defaultScopes = fresh.selected_project_defaults.filter((row) => row.client_scope_id
        && row.project_id && projectSet.has(row.project_id)).map((row) => row.client_scope_id!);
      if (sameChange) {
        setSelectedDefaults((prior) => prior.filter((id) => defaultScopes.includes(id)));
      } else {
        setSelectedDefaults(defaultScopes);
        setKeepIds([]);
      }
      setSelectedProjects(reviewedIntent.intent === 'project_address_correction' ? [...reviewedIntent.project_ids] : []);
      setLoading(false);
    }).catch((cause: Error) => {
      if (!live) return;
      setError(`Could not review this change: ${cause.message}`);
      setLoading(false);
    });
    return () => { live = false; };
  // Parent editors may construct equivalent intent/ID arrays on each render.
  // Reset only when the reviewed meaning changes, not object identity.
  }, [intentKey, focusedInvoiceId, projectKey, baseIntentKey]);

  const focusedProjects = useMemo(() => new Set(projectKey ? projectKey.split('|') : []), [projectKey]);
  const inScope = plan?.candidates.filter((row) => row.invoice_id === focusedInvoiceId
    || focusedProjects.has(row.project_id)) ?? [];
  const outside = plan?.candidates.filter((row) => row.invoice_id !== focusedInvoiceId
    && !focusedProjects.has(row.project_id)) ?? [];
  const sourceOnly = plan?.source_actions.filter((row) =>
    !plan.candidates.some((candidate) => candidate.invoice_id === row.invoice_id)) ?? [];
  const selectedSet = new Set(selected);
  const requiredAck = [...new Set((plan ? [...plan.candidates, ...plan.source_actions] : [])
    .filter((row) => selectedSet.has(row.invoice_id) && row.requires_post_send_acknowledgement)
    .map((row) => row.invoice_id))];
  const rowsWithRecipientEffects = plan
    ? [...plan.candidates, ...plan.source_actions]
    : [];
  // M1: a saved person she ticked "Keep on {project} invoices" stays on that project's moved invoices (the server
  // points their row at the new client's contact), so they are not removals and need no approval.
  const keptOnProject = (projectId: string) => new Set(isMove
    ? (plan?.project_recipients ?? [])
      .filter((entry) => entry.project_id === projectId && selectedDefaults.includes(entry.client_scope_id))
      .flatMap((entry) => entry.people.map((person) => person.contact_id))
      .filter((id) => keepIds.includes(id))
    : []);
  const effectsAfterKeep = (row: { project_id: string; recipient_effects?: RecipientEffects | null }) => {
    const effects = row.recipient_effects;
    if (!effects) return effects;
    const kept = keptOnProject(row.project_id);
    if (kept.size === 0) return { ...effects, kept_on_move: [] as RecipientEffects['removed_other_linked'] };
    return {
      ...effects,
      removed_other_linked: effects.removed_other_linked.filter((person) => !person.contact_id || !kept.has(person.contact_id)),
      kept_on_move: effects.removed_other_linked.filter((person) => person.contact_id && kept.has(person.contact_id)),
    };
  };
  const removalsOf = (row: { project_id: string; recipient_effects?: RecipientEffects | null }) =>
    effectsAfterKeep(row)?.removed_other_linked ?? [];
  const requiredRecipientRemovalAck = [...new Set(rowsWithRecipientEffects
    .filter((row) => selectedSet.has(row.invoice_id) && removalsOf(row).length > 0)
    .map((row) => row.invoice_id))];
  const ackComplete = requiredAck.every((id) => acknowledged.includes(id))
    && requiredRecipientRemovalAck.every((id) => acknowledgedRecipientRemovals.includes(id));
  const missingFutureDefaultTarget = plan?.selected_project_defaults.some((row) =>
    !defaultTarget(row).name
      || Boolean(row.before_organization_id && !row.before_organization_name)
      || Boolean(row.after_organization_id && !row.after_organization_name)) ?? false;
  const approvedSource = intent.intent === 'shared_correction'
    || (intent.intent === 'identity_default' && intent.default_level === 'this_client');
  const hasAction = approvedSource || selected.length > 0
    || selectedDefaults.length > 0 || selectedProjects.length > 0;
  const futureDefaultCount = selectedDefaults.length + selectedProjects.length
    + (intent.intent === 'identity_default' && intent.default_level === 'this_client' ? 1 : 0);

  // ---- R2: plain-words names for the project move (AC7, AC8, AC9, AC10) ----
  const projectParts = useMemo(() => {
    const parts = new Map<string, { number?: string | null; name?: string | null }>();
    for (const track of plan?.target_tracks ?? []) {
      parts.set(track.project_id, { number: track.project_number, name: track.project_name });
    }
    for (const row of plan?.selected_project_defaults ?? []) {
      if (row.project_id) parts.set(row.project_id, { number: row.project_number, name: row.project_name });
    }
    for (const row of plan?.candidates ?? []) {
      if (!parts.has(row.project_id)) parts.set(row.project_id, { name: row.project_name });
    }
    return parts;
  }, [plan]);
  const labelOfProject = (projectId: string) =>
    projectLabelOf(projectParts.get(projectId)?.number, projectParts.get(projectId)?.name);
  const firstDefault = plan?.selected_project_defaults[0];
  const sourceClientName = firstDefault?.before_organization_name
    || plan?.candidates[0]?.before.client_display_name.value || 'the old client';
  const targetClientName = firstDefault?.after_organization_name
    || plan?.target_tracks?.[0]?.target_organization_name || 'the new client';
  const needsTrackChoice = (plan?.target_tracks ?? []).some((track) => track.action === 'choose');
  const keptPeople = (plan?.project_recipients ?? [])
    .filter((entry) => selectedDefaults.includes(entry.client_scope_id))
    .flatMap((entry) => entry.people.map((person) => person.contact_id))
    .filter((id) => keepIds.includes(id));

  // Keep the named consequences in the review and the final action compact.
  const invoiceTotal = selected.length;
  const sharedFields = intent.intent === 'shared_correction' && intent.establishment_id == null
    ? Object.keys(intent.fields) : [];
  const renamesClient = sharedFields.includes('display_name') || sharedFields.includes('legal_name');
  const changesAddress = sharedFields.includes('billing_address') || sharedFields.includes('address');
  const movedProjectIds = (plan?.selected_project_defaults ?? [])
    .filter((row) => row.client_scope_id && selectedDefaults.includes(row.client_scope_id) && row.project_id)
    .map((row) => row.project_id as string);
  const scopeSummary = sharedFields.length > 0
    ? `${renamesClient ? 'Rename client' : 'Change client address'} and update ${plural(invoiceTotal, 'invoice', 'invoices')}`
    : isMove && movedProjectIds.length > 0 && !missingFutureDefaultTarget
      ? `Move ${listNames(movedProjectIds.map(labelOfProject))} to ${targetClientName} and update ${plural(invoiceTotal, 'invoice', 'invoices')}`
      : isMove && !missingFutureDefaultTarget && firstDefault?.after_organization_name
        ? `Change client on ${plural(invoiceTotal, 'invoice', 'invoices')} to ${targetClientName}`
        : `${approvedSource ? 'Update the shared source. ' : ''}Update ${plural(invoiceTotal, 'invoice', 'invoices')}. ${futureDefaultCount > 0 ? `Update ${plural(futureDefaultCount, 'future default', 'future defaults')}.` : approvedSource ? 'New invoices may use these updated details.' : 'Future defaults stay unchanged.'}`;

  // R2 AC8: a disabled Apply says why, and the click handler asks the same question again.
  function applyBlockReason(): string | null {
    if (!plan) return null;
    if (!ackComplete) return 'Tick the box under each invoice that needs your OK before you apply.';
    if (!hasAction) return 'Choose at least one invoice or default to change.';
    if (missingFutureDefaultTarget) {
      return 'The project or client name is missing, so nothing can be applied. Close this and open it again.';
    }
    if (needsTrackChoice) return 'Choose which billing track to use.';
    if (error && !pendingRequest && !stale) return 'Fix the problem shown above, then try again.';
    return null;
  }
  const applyBlock = saving ? null : applyBlockReason();

  function toggle(id: string, values: string[], setValues: (next: string[]) => void) {
    setValues(values.includes(id) ? values.filter((value) => value !== id) : [...values, id]);
    if (!pendingRequest) {
      setError(null);
      setStale(false);
    }
  }

  function toggleInvoice(id: string) {
    setSelected((prior) => prior.includes(id) ? prior.filter((value) => value !== id) : [...prior, id]);
    // A removal decision belongs to a selected invoice in the current plan.
    setAcknowledgedRecipientRemovals((prior) => prior.filter((invoiceId) => invoiceId !== id));
    if (!pendingRequest) {
      setError(null);
      setStale(false);
    }
  }

  async function apply() {
    if (!plan || applyBlockReason() !== null) return;
    // After an uncertain response, the only safe retry is byte-for-byte the
    // original intent, selections and operation key. Do not refresh first.
    const request: InvoiceChangeApplyRequest = pendingRequest ?? {
      plan_id: plan.plan_id, intent: effectiveIntent, selected_invoice_ids: selected,
      selected_project_default_scope_ids: selectedDefaults,
      selected_project_ids: selectedProjects,
      apply_source_update: intent.intent === 'shared_correction'
        || (intent.intent === 'identity_default' && intent.default_level === 'this_client'),
      acknowledge_sent_invoice_ids: acknowledged.filter((id) => selected.includes(id)),
      acknowledge_recipient_removal_invoice_ids: acknowledgedRecipientRemovals
        .filter((id) => selected.includes(id) && requiredRecipientRemovalAck.includes(id)),
      keep_project_recipient_contact_ids: keptPeople,
      operation_key: crypto.randomUUID(),
    };
    setPendingRequest(request);
    setSaving(true);
    setError(null);
    setBlockedReservations([]);
    try {
      const result = await api.invoices.applyChange(request);
      setPendingRequest(null);
      await Promise.all(['invoices', 'organizations', 'clients', 'projects', 'project-scopes',
        'organization-establishments'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
      onApplied(result);
    } catch (cause) {
      const failure = cause as ApiFailure;
      const detail = failure.data?.error ?? failure.data?.detail;
      if (detail?.code === 'stale_plan') {
        setPendingRequest(null);
        const parsed = invoiceChangePlanSchema.safeParse(detail.comparison);
        if (parsed.success) {
          const fresh = parsed.data;
          const valid = new Set([...fresh.candidates.map((row) => row.invoice_id),
            ...fresh.source_actions.map((row) => row.invoice_id)]);
          setSelected((prior) => prior.filter((id) => valid.has(id)));
          setAcknowledged([]); // Changed sent impact needs a fresh acknowledgement.
          setAcknowledgedRecipientRemovals([]); // Recipient names and removal membership are part of the fresh impact.
          setSelectedDefaults((prior) => prior.filter((id) =>
            fresh.selected_project_defaults.some((row) => row.client_scope_id === id)));
          setPlan(fresh);
          setStale(true);
          setError('The impact changed while you were reviewing. Check the refreshed invoices and defaults before applying again. Newly affected invoices were left unchecked.');
        } else {
          setPlan(null);
          setStale(false);
          setError('The reviewed target is no longer available. Close this review and choose the source again. Nothing was changed.');
        }
      } else if (detail?.code === 'checkout_reconciliation_required') {
        setPendingRequest(null);
        setBlockedReservations(parseBlockingReservations(detail.comparison));
        setError('A payment link for one of these invoices has an unconfirmed outcome, so billing details cannot change yet. Use Check with payment provider below, then apply again. Nothing was changed.');
      } else {
        if (failure.status && failure.status >= 400 && failure.status < 500
            && detail?.code !== 'apply_retryable_lock_conflict') {
          setPendingRequest(null);
          setError(`The server refused this change; no reviewed write was accepted: ${failure.message}`);
        } else {
          setError(`Could not confirm whether this change completed. Retry uses the same operation key and selection: ${failure.message}`);
        }
      }
    } finally {
      setSaving(false);
    }
  }

  const invoiceRow = (row: InvoiceChangePlan['candidates'][number]) => (
    <div key={row.invoice_id} className="rounded border p-3" data-testid={`review-invoice-${row.invoice_id}`}>
      <label className="flex items-start gap-2">
        <Checkbox checked={selectedSet.has(row.invoice_id)} disabled={Boolean(pendingRequest)}
          onCheckedChange={() => toggleInvoice(row.invoice_id)} />
        <span><strong>Invoice {row.invoice_number}</strong> · {row.project_name || row.project_id} · {row.status}
          {row.requires_post_send_acknowledgement && ' · previously recorded as sent'}</span>
      </label>
      <ul className="ml-7 text-sm text-muted-foreground">{describedChanges(row).map((line) => <li key={line}>{line}</li>)}</ul>
      {followDefaultDetails(row, intent)}
      {row.status === 'cancelled' && (
        <p className="ml-7 mt-1 text-sm text-slate-700" data-testid={`cancelled-line-${row.invoice_id}`}>
          Cancelled. Not changed unless you tick it.
        </p>
      )}
      {isMove && carefulLines(row).map((line) => <p key={line} className="ml-7 mt-1 text-sm text-slate-700">{line}</p>)}
      {/* R3 AC9: the recipient box describes a change that will happen, so it shows only on a ticked invoice. */}
      {selectedSet.has(row.invoice_id) && recipientImpact(effectsAfterKeep(row))}
      {(intent.intent === 'project_assignment' || intent.intent === 'invoice_assignment')
        && selectedSet.has(row.invoice_id) && removalsOf(row).length > 0 && (
        <p className="ml-7 mt-1 text-sm text-slate-700" data-testid={`recipient-removal-line-${row.invoice_id}`}>
          {(() => {
            // TO people first, then CC, each by name, so the sentence reads the same every time.
            const rank = (role: string | null) => (String(role ?? '').toLowerCase() === 'to' ? 0 : 1);
            const names = [...removalsOf(row)]
              .sort((a, b) => rank(a.role) - rank(b.role) || (a.name || '').localeCompare(b.name || ''))
              .map((person) => person.name || person.email || 'A saved contact');
            const client = row.before.client_display_name.value || sourceClientName;
            return `${listNames(names)} ${names.length === 1 ? `is a ${client} contact` : `are ${client} contacts`} and will be removed from this invoice. Nobody is added in their place. Add the right people on the invoice after the move.`;
          })()}
        </p>
      )}
      {selectedSet.has(row.invoice_id) && row.requires_post_send_acknowledgement && (
        <label className="ml-7 mt-2 flex gap-2 text-sm">
          <Checkbox aria-label={`Acknowledge sent invoice ${row.invoice_number}`} checked={acknowledged.includes(row.invoice_id)} disabled={Boolean(pendingRequest)}
            onCheckedChange={() => toggle(row.invoice_id, acknowledged, setAcknowledged)} />
          I understand this changes the current billing details. Any earlier recorded document remains separate; some earlier sends have no known archived copy.
        </label>
      )}
      {selectedSet.has(row.invoice_id) && removalsOf(row).length > 0 && (
        <label className="ml-7 mt-2 flex gap-2 text-sm">
          <Checkbox aria-label={`Approve recipient removals for invoice ${row.invoice_number}`}
            checked={acknowledgedRecipientRemovals.includes(row.invoice_id)} disabled={Boolean(pendingRequest)}
            onCheckedChange={() => toggle(row.invoice_id, acknowledgedRecipientRemovals,
              setAcknowledgedRecipientRemovals)} />
          I reviewed the recipients above and approve removing the listed recipients from this invoice.
        </label>
      )}
    </div>
  );

  const visible = (rows: InvoiceChangePlan['candidates']) => rows.filter((row) =>
    `${row.invoice_number} ${row.project_name ?? ''} ${row.status}`.toLowerCase().includes(search.toLowerCase()));

  return <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
    <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="invoice-change-review">
      <DialogHeader><DialogTitle>Review billing change</DialogTitle>
        <DialogDescription>Choose the invoices and future defaults that should change. Unchecked invoices keep their current details.</DialogDescription>
      </DialogHeader>
      {loading && <p>Checking affected invoices and future defaults…</p>}
      {error && <p role="alert" className="text-destructive">{error}</p>}
      {blockedReservations.length > 0 && (
        <ReservationReconcileList
          reservations={blockedReservations}
          onSettled={(result) => {
            setBlockedReservations([]);
            setError(`${result.message} Apply the change again.`);
          }}
        />
      )}
      {plan && <>
        {stale && <p className="rounded bg-amber-50 p-2">Impact changed. Review this updated plan before continuing.</p>}
        {plan.source_changes && plan.source_changes.length > 0 && <section data-testid="review-source-changes">
          <h3 className="font-medium">Source record changes</h3>
          {plan.source_changes.map((change, index) => <p key={`${change.source_id}-${change.field}-${index}`}>
            {change.project_name || change.source_type} · {change.field.replace('_', ' ')}:
            {' '}{String(change.before ?? '') || '(empty)'} → {String(change.after ?? '') || '(empty)'}
          </p>)}
        </section>}
        {isMove ? <p data-testid="review-counts">
          {`${plural(inScope.length, 'invoice', 'invoices')} on ${listNames([...focusedProjects].map(labelOfProject)) || 'the chosen projects'}.`}
          {outside.length > 0 && ` ${plural(outside.length, 'invoice', 'invoices')} on other projects ${outside.length === 1 ? 'is' : 'are'} not changed unless you tick ${outside.length === 1 ? 'it' : 'them'}.`}
        </p> : <p>{plan.candidate_count} affected invoices; {selected.length} selected; {plan.candidate_count - selected.filter((id) =>
          plan.candidates.some((row) => row.invoice_id === id)).length} will keep their current details.</p>}
        <p>{plural(futureDefaultCount, 'future default', 'future defaults')} will change.</p>
        <input aria-label="Filter affected invoices" placeholder="Filter invoice or project" value={search}
          onChange={(event) => setSearch(event.target.value)} className="w-full rounded border p-2" />
        <div className="space-y-2">{visible(inScope).map(invoiceRow)}</div>
        {outside.length > 0 && <div>
          <Button variant="outline" onClick={() => setShowOtherProjects(!showOtherProjects)}>
            {showOtherProjects ? 'Hide' : 'Show'} {outside.length} affected invoices in other projects
          </Button>
          {showOtherProjects && <div className="mt-2 space-y-2">{visible(outside).map(invoiceRow)}</div>}
        </div>}
        {sourceOnly.length > 0 && <section>
          <h3 className="font-medium">Source or inheritance changes without a printed value change</h3>
          {sourceOnly.map((action) => <div key={action.invoice_id} data-testid={`review-source-action-${action.invoice_id}`}>
            <label className="flex gap-2 p-2">
              <Checkbox checked={selectedSet.has(action.invoice_id)} disabled={Boolean(pendingRequest)}
                onCheckedChange={() => toggleInvoice(action.invoice_id)} />
              Invoice {action.invoice_number || action.invoice_id} · {action.project_name || action.project_id || 'Project'}
              {' '}· {action.status || 'status unknown'}: {action.fields.join(', ')}
              {action.requires_post_send_acknowledgement && ' · previously recorded as sent'}
            </label>
            {followDefaultDetails(action, intent)}
            {recipientImpact(effectsAfterKeep(action))}
            {selectedSet.has(action.invoice_id) && action.requires_post_send_acknowledgement && (
              <label className="ml-7 mt-2 flex gap-2 text-sm">
                <Checkbox aria-label={`Acknowledge sent invoice ${action.invoice_number}`}
                  checked={acknowledged.includes(action.invoice_id)} disabled={Boolean(pendingRequest)}
                  onCheckedChange={() => toggle(action.invoice_id, acknowledged, setAcknowledged)} />
                I understand this changes the current billing details. Any earlier recorded document remains separate; some earlier sends have no known archived copy.
              </label>
            )}
            {selectedSet.has(action.invoice_id) && removalsOf(action).length > 0 && (
              <label className="ml-7 mt-2 flex gap-2 text-sm">
                <Checkbox aria-label={`Approve recipient removals for invoice ${action.invoice_number}`}
                  checked={acknowledgedRecipientRemovals.includes(action.invoice_id)} disabled={Boolean(pendingRequest)}
                  onCheckedChange={() => toggle(action.invoice_id, acknowledgedRecipientRemovals,
                    setAcknowledgedRecipientRemovals)} />
                I reviewed the recipients above and approve removing the listed recipients from this invoice.
              </label>
            )}
          </div>)}
        </section>}
        {plan.selected_project_defaults.length > 0 && <section className="space-y-2">
          <h3 className="font-medium">Future project and client defaults</h3>
          {plan.selected_project_defaults.map((item, index) => {
            const id = item.client_scope_id || item.project_id;
            const target = defaultTarget(item);
            const changes = futureDefaultChanges(item, intent);
            if (!id) return <div key={`${item.organization_id || 'client'}-${index}`}
              data-testid={`future-default-client-${item.organization_id || 'unknown'}`} className="rounded border p-2">
              <p className="font-medium">{target.name || target.missingLabel}</p>
              {changes.length > 0 && <ul className="text-sm">{changes.map((change) => <li key={change}>{change}</li>)}</ul>}
            </div>;
            const values = item.client_scope_id ? selectedDefaults : selectedProjects;
            const setter = item.client_scope_id ? setSelectedDefaults : setSelectedProjects;
            return <div key={`${id}-${index}`} data-testid={`future-default-project-${id}`} className="rounded border p-2">
              <label className="flex gap-2">
                <Checkbox checked={values.includes(id)} disabled={Boolean(pendingRequest)
                  || intent.intent === 'project_address_correction' || !target.name}
                  onCheckedChange={() => toggle(id, values, setter)} />
                <span>
                  <strong>{target.name || target.missingLabel}</strong>
                </span>
              </label>
              {changes.length > 0 && <ul className="ml-6 text-sm">{changes.map((change) => <li key={change}>{change}</li>)}</ul>}
            </div>;
          })}
          {missingFutureDefaultTarget && <p role="alert" className="text-destructive">
            A project or client name is unavailable for this future default. Close and reopen the review after refreshing the target details; nothing can be applied until the target is clear.
          </p>}
        </section>}
        {(plan.target_tracks ?? []).length > 0 && <section className="space-y-2" data-testid="target-tracks">
          {(plan.target_tracks ?? []).map((track) => {
            const key = track.source_client_scope_id ?? track.project_id;
            const where = projectLabelOf(track.project_number, track.project_name);
            const client = track.target_organization_name || targetClientName;
            if (track.action === 'choose') {
              const label = `Which ${client} billing track on ${where}?`;
              return <div key={key} data-testid={`target-track-${key}`} className="rounded border p-2">
                <p className="text-sm font-medium" id={`track-choice-${key}`}>{label}</p>
                <div role="radiogroup" aria-label={label} className="mt-1 space-y-1">
                  {track.candidates.map((candidate) => <label key={candidate.id} className="flex items-center gap-2 text-sm">
                    <input type="radio" name={`track-${key}`} checked={trackChoices[key] === candidate.id}
                      disabled={Boolean(pendingRequest)}
                      onChange={() => { setTrackChoices((prior) => ({ ...prior, [key]: candidate.id })); setError(null); }} />
                    {candidate.scope_type_name || 'Billing track'}{candidate.is_primary ? ' (main track)' : ''}
                  </label>)}
                </div>
              </div>;
            }
            return <p key={key} className="text-sm" data-testid={`target-track-${key}`}>
              {track.action === 'reuse'
                ? `Uses the existing ${client} billing track on ${where}.`
                : `Adds a new ${client} billing track on ${where}, type ${track.target_scope_type_name || 'Client'}.`}
            </p>;
          })}
        </section>}
        {isMove && (plan.project_recipients ?? []).filter((entry) => selectedDefaults.includes(entry.client_scope_id)).map((entry) => {
          const short = entry.project_number || entry.project_name || 'this project';
          return <section key={entry.client_scope_id} className="space-y-2 rounded border p-3"
            data-testid={`project-people-${entry.client_scope_id}`}>
            {entry.people.length === 0
              ? <p className="text-sm">{`${short} has no saved contacts. Add them on the next invoice.`}</p>
              : <>
                <p className="text-sm">
                  {`${plural(entry.people.length, 'person is', 'people are')} saved for ${short}. They are contacts of ${sourceClientName}. Tick anyone who should still get ${short}'s invoices from ${targetClientName}.`}
                </p>
                <ul className="space-y-1">
                  {entry.people.map((person) => <li key={person.contact_id} data-testid={`keep-person-${person.contact_id}`}
                    className="flex flex-wrap items-center gap-2 text-sm">
                    <Checkbox aria-label={`Keep on ${short} invoices`} checked={keepIds.includes(person.contact_id)}
                      disabled={Boolean(pendingRequest)}
                      onCheckedChange={() => toggle(person.contact_id, keepIds, setKeepIds)} />
                    <span className="font-medium">{person.name}</span>
                    <span className="text-slate-600">{person.email}</span>
                    <span className="rounded bg-slate-100 px-1.5 text-xs font-semibold">{person.role.toUpperCase()}</span>
                    <span className="text-slate-600">{`Keep on ${short} invoices`}</span>
                  </li>)}
                </ul>
                <p className="text-sm text-slate-600" data-testid={`keep-needs-to-${entry.client_scope_id}`}>
                  An invoice always needs a TO. If you keep only CC people, the first one shows as TO on the next invoice until you add a TO.
                </p>
              </>}
          </section>;
        })}
        {(plan.combined_away_invoices?.length ?? 0) > 0 && <p className="text-sm" data-testid="combined-away-note">
          {plan.combined_away_invoices!.map((row) => `${row.invoice_number ?? 'An invoice'} was combined into ${row.merged_into_invoice_number ?? 'another invoice'}`).join('; ')}.
          {' '}It is not listed here because it is history and keeps the details it had. The change applies to the invoice it was combined into when that invoice is ticked in the list.
        </p>}
        {plan.unresolved_unscoped_invoices.length > 0 && <p className="text-sm">
          {plan.unresolved_unscoped_invoices.length} older invoices have no client scope and need a separate invoice review.
        </p>}
        {sharedFields.length > 0 && plan.shared_source && <div className="space-y-1 text-sm" data-testid="shared-source-sentence">
          {renamesClient && <p>{`The name changes everywhere ${plan.shared_source.organization_name || 'this client'} is used, including ${plural(plan.shared_source.project_count, 'project', 'projects')}.`}</p>}
          {changesAddress && <p>{`This address changes everywhere ${plan.shared_source.organization_name || 'this client'} is used, including ${plural(plan.shared_source.project_count, 'project', 'projects')}.`}</p>}
        </div>}
        <p className="text-sm text-slate-700" data-testid="apply-scope-summary">{scopeSummary}</p>
        {/* Full names stay in the scrollable review; counts and the action stay within reach on a phone. */}
        <div className="sticky bottom-0 -mx-6 -mb-6 flex flex-col items-end gap-2 border-t bg-white px-6 py-3">
          <p className="w-full text-sm text-slate-700" data-testid="apply-selection-summary">
            {plural(invoiceTotal, 'invoice selected', 'invoices selected')}.{' '}
            {futureDefaultCount > 0 ? plural(futureDefaultCount, 'future default', 'future defaults') + ' will change.' : !approvedSource && 'Future defaults stay unchanged.'}
            {approvedSource && ' Shared billing details will also change; new invoices may use them.'}
          </p>
          {applyBlock && <p className="text-right text-sm text-slate-700" role="status" data-testid="apply-reason">{applyBlock}</p>}
          <div className="flex w-full flex-wrap justify-end gap-2"><Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            {/* Keep the action short; the visible scope summary carries the full names and consequences. */}
            <Button onClick={apply} disabled={saving || applyBlock !== null} className="h-auto whitespace-normal py-2 text-left">
              {saving ? 'Applying…' : pendingRequest ? 'Retry exact request' : 'Apply changes'}
            </Button></div>
        </div>
      </>}
    </DialogContent>
  </Dialog>;
}
